import { useState } from 'react';
import { SPORTS } from '../../../data/sports';
import { SPARRING_PARTNER_LEVELS, GRAPPLING_PARTNER_LEVELS, STRIKING_PARTNER_LEVELS, MMA_PARTNER_LEVELS } from '../../../data/constants';
import { useToast } from '../../../context/ToastContext';
import { numericProps } from '../../../utils/numericInput';

function korParticle(name) {
    if (!name) return '로';
    const last = name[name.length - 1];
    const code = last.charCodeAt(0);
    if (code < 0xAC00 || code > 0xD7A3) return '로';
    const jongseong = (code - 0xAC00) % 28;
    if (jongseong === 0 || jongseong === 8) return '로';
    return '으로';
}

function getSparringMeta(sport) {
    const cat = SPORTS[sport]?.category;
    const isBjj = SPORTS[sport]?.isBjj;
    if (isBjj) return { levels: SPARRING_PARTNER_LEVELS, metricLabel: '탭 횟수', metricPlaceholder: '0' };
    if (cat === 'grappling') return { levels: GRAPPLING_PARTNER_LEVELS, metricLabel: '테이크다운/한판 횟수', metricPlaceholder: '0' };
    if (cat === 'striking') return { levels: STRIKING_PARTNER_LEVELS, metricLabel: '다운 횟수', metricPlaceholder: '0' };
    return { levels: MMA_PARTNER_LEVELS, metricLabel: '피니시/KD 횟수', metricPlaceholder: '0' };
}

function SparringRoundsEditor({ rounds, onChange, friends = [], sport }) {
    const showToast = useToast();
    const [queries, setQueries] = useState(() => rounds.map(() => ''));
    const meta = getSparringMeta(sport);

    const add = () => {
        onChange([...rounds, { opponentLevel: '', result: '', tapouts: '', roundCount: '1', roundMinutes: '', notes: '', opponent: null }]);
        setQueries(prev => [...prev, '']);
    };
    const remove = i => {
        onChange(rounds.filter((_, idx) => idx !== i));
        setQueries(prev => prev.filter((_, idx) => idx !== i));
    };
    const update = (i, key, val) => onChange(rounds.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
    const setQuery = (i, val) => setQueries(prev => prev.map((q, idx) => idx === i ? val : q));
    const setOpponent = (i, opponent) => { update(i, 'opponent', opponent); setQuery(i, ''); };

    return (
        <>
            {rounds.map((r, i) => {
                const q = (queries[i] || '').trim();
                const matchingFriends = q
                    ? friends.filter(f =>
                        f.nickname?.toLowerCase().includes(q.toLowerCase()) ||
                        f.userId?.toLowerCase().includes(q.toLowerCase())
                    ).slice(0, 4)
                    : [];
                const exactMatch = friends.some(f => f.nickname?.toLowerCase() === q.toLowerCase());

                return (
                    <div key={i} style={{ marginBottom: 12, padding: '12px 14px', background: 'var(--bg2)', borderRadius: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)' }}>스파링 {i + 1}</span>
                            <button className="remove-btn" onClick={() => remove(i)}>×</button>
                        </div>

                        <div className="metric-grid" style={{ marginBottom: 8 }}>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">라운드 수</label>
                                <input className="form-input" placeholder="1"
                                    {...numericProps(r.roundCount || '', v => update(i, 'roundCount', v), showToast)} />
                            </div>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">경기 시간(분)</label>
                                <input className="form-input" placeholder="5"
                                    {...numericProps(r.roundMinutes || '', v => update(i, 'roundMinutes', v), showToast)} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                            {['승', '패', '무'].map(res => (
                                <button key={res}
                                    className={`toggle-btn ${r.result === res ? 'active' : ''}`}
                                    style={{ flex: 1, padding: '6px 0', fontSize: 14 }}
                                    onClick={() => update(i, 'result', r.result === res ? '' : res)}
                                >{res}</button>
                            ))}
                        </div>

                        <select className="form-input"
                            style={{ marginBottom: 8, color: r.opponentLevel ? 'var(--text1)' : 'var(--text3)' }}
                            value={r.opponentLevel || ''}
                            onChange={e => update(i, 'opponentLevel', e.target.value)}>
                            <option value="">상대 레벨 (선택)</option>
                            {meta.levels.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>

                        {r.opponent ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '8px 12px', background: 'var(--bg3)', borderRadius: 8 }}>
                                <span style={{ flex: 1, fontSize: 13, color: 'var(--text1)' }}>
                                    {r.opponent.uid && <span style={{ fontSize: 10, color: 'var(--accent)', marginRight: 4 }}>@</span>}
                                    {r.opponent.nickname || r.opponent.name}
                                </span>
                                <button className="remove-btn" onClick={() => update(i, 'opponent', null)}>×</button>
                            </div>
                        ) : (
                            <div style={{ marginBottom: 8 }}>
                                <input
                                    className="form-input"
                                    placeholder="상대 이름 검색 또는 직접 입력"
                                    value={queries[i] || ''}
                                    onChange={e => setQuery(i, e.target.value)}
                                    style={{ marginBottom: (matchingFriends.length > 0 || (q && !exactMatch)) ? 6 : 0 }}
                                />
                                {matchingFriends.map(f => (
                                    <button key={f.uid}
                                        onClick={() => setOpponent(i, { uid: f.uid, userId: f.userId, nickname: f.nickname })}
                                        style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            width: '100%', padding: '8px 12px', marginBottom: 4,
                                            background: 'var(--bg3)', border: 'none', borderRadius: 8,
                                            color: 'var(--text1)', fontSize: 13, cursor: 'pointer', textAlign: 'left',
                                        }}>
                                        <span>
                                            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{f.nickname}</span>
                                            <span style={{ color: 'var(--text3)', fontSize: 11, marginLeft: 6 }}>@{f.userId}</span>
                                        </span>
                                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                                            {f.nickname}{korParticle(f.nickname)} 상대 추가
                                        </span>
                                    </button>
                                ))}
                                {q && !exactMatch && (
                                    <button
                                        onClick={() => setOpponent(i, { name: q })}
                                        style={{
                                            display: 'block', width: '100%', padding: '8px 12px', marginBottom: 4,
                                            background: 'var(--bg3)', border: '1px dashed var(--text3)', borderRadius: 8,
                                            color: 'var(--text2)', fontSize: 13, cursor: 'pointer', textAlign: 'left',
                                        }}>
                                        "{q}"{korParticle(q)} 상대 추가
                                    </button>
                                )}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                            <input className="form-input" placeholder={meta.metricPlaceholder} style={{ flex: 1, padding: 8 }}
                                {...numericProps(r.tapouts || '', v => update(i, 'tapouts', v), showToast)} />
                            <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{meta.metricLabel}</span>
                        </div>
                        <textarea className="form-input" rows={2} placeholder="이 스파링 메모 (선택)"
                            style={{ resize: 'none' }}
                            value={r.notes || ''}
                            onChange={e => update(i, 'notes', e.target.value)} />
                    </div>
                );
            })}
            <button className="add-set-btn" style={{ width: '100%' }} onClick={add}>+ 스파링 기록 추가</button>
        </>
    );
}

export default function SparringSection({ form, set, friends, sparRounds, setSparRounds, sparMin, setSparMin }) {
    const showToast = useToast();
    const sparringStats = (form.sparringRoundsList || []).reduce((acc, round) => {
        acc.count += +round.roundCount || 0;
        acc.minutes += (+round.roundCount || 0) * (+round.roundMinutes || 0);
        return acc;
    }, { count: 0, minutes: 0 });
    const hasTimedDetailedSparring = (form.sparringRoundsList || []).some(r => r.roundCount || r.roundMinutes);

    const syncSparringFriendsToBuddies = (nextRounds) => {
        const prevOpponentUids = new Set((form.sparringRoundsList || []).map(r => r.opponent?.uid).filter(Boolean));
        const nextOpponentUids = new Set((nextRounds || []).map(r => r.opponent?.uid).filter(Boolean));
        const friendMap = new Map((friends || []).map(f => [f.uid, f]));
        const removedUids = [...prevOpponentUids].filter(uid => !nextOpponentUids.has(uid));
        const nextBuddies = (form.buddies || []).filter(b => !removedUids.includes(b.uid));
        nextOpponentUids.forEach(uid => {
            if (nextBuddies.some(b => b.uid === uid)) return;
            const friend = friendMap.get(uid);
            if (friend) nextBuddies.push({ uid: friend.uid, userId: friend.userId, nickname: friend.nickname });
        });
        set('buddies', nextBuddies);
        set('sparringRoundsList', nextRounds);
    };

    return (
        <>
            <div className="form-group">
                <label className="form-label">스파링</label>
                <div className="toggle-row">
                    <button className={`toggle-btn ${form.sparring ? 'active' : ''}`} onClick={() => set('sparring', true)}>했음</button>
                    <button className={`toggle-btn ${!form.sparring ? 'active' : ''}`} onClick={() => set('sparring', false)}>안 함</button>
                </div>
            </div>
            {form.sparring && (
                <>
                    <div className="metric-grid">
                        <div className="form-group">
                            <label className="form-label">총 스파링 횟수</label>
                            <input className="form-input" placeholder="5"
                                {...numericProps(hasTimedDetailedSparring ? String(sparringStats.count || '') : sparRounds, setSparRounds, showToast)}
                                disabled={hasTimedDetailedSparring} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">총 스파링 시간(분)</label>
                            <input className="form-input" placeholder="25"
                                {...numericProps(hasTimedDetailedSparring ? String(sparringStats.minutes || '') : sparMin, setSparMin, showToast)}
                                disabled={hasTimedDetailedSparring} />
                        </div>
                    </div>
                    {hasTimedDetailedSparring && (
                        <div className="form-hint">스파링 기록의 라운드 수 × 경기 시간으로 자동 계산돼요.</div>
                    )}
                    <div className="form-group">
                        <label className="form-label">스파링 기록 (선택)</label>
                        <SparringRoundsEditor
                            rounds={form.sparringRoundsList || []}
                            onChange={syncSparringFriendsToBuddies}
                            friends={friends}
                            sport={form.sport}
                        />
                    </div>
                </>
            )}
        </>
    );
}
